/**
 * Pick locale for PII PDF generation. Browser page translation (e.g. Chrome)
 * often does not change navigator.language or set googtrans; the DOM text
 * is translated instead. We infer script from a visible probe node and
 * prefer non-English entries in navigator.languages when present.
 */

const SUPPORTED_PII_PDF_LANG_PREFIXES = ["ja", "zh", "ko", "de", "nl", "en"];

const normalizeLang = (lang) => String(lang || "").trim().toLowerCase();

export const isSupportedPiiPdfLanguage = (lang) => {
  const normalized = normalizeLang(lang);
  return SUPPORTED_PII_PDF_LANG_PREFIXES.some((prefix) =>
    normalized.startsWith(prefix)
  );
};

const isEnglishLang = (lang) => {
  const n = normalizeLang(lang);
  return n === "" || n.startsWith("en");
};

const getBrowserTranslatorTargetLanguage = () => {
  if (typeof document === "undefined") {
    return "";
  }

  const cookieValue = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("googtrans="));

  if (!cookieValue) {
    return "";
  }

  const raw = decodeURIComponent(cookieValue.split("=")[1] || "");
  const parts = raw.split("/").filter(Boolean);
  const targetLang = parts[parts.length - 1] || "";
  return targetLang;
};

/**
 * Infer PDF locale from text Chrome (or another translator) rewrote in the DOM.
 * Uses a visible [data-pdf-lang-probe] node (e.g. page title) so translation runs on it.
 */
export const inferPdfLanguageFromProbeText = (text) => {
  const s = String(text || "");
  if (!s.trim()) {
    return "";
  }
  // Japanese: hiragana, katakana (common when EN UI is translated to JA)
  if (/[\u3040-\u309F\u30A0-\u30FF]/.test(s)) {
    return "ja-JP";
  }
  if (/[\uAC00-\uD7AF]/.test(s)) {
    return "ko";
  }
  // Han only: likely zh-CN when no Japanese kana (rough; avoids false ja from kanji alone)
  if (/[\u4E00-\u9FFF]/.test(s) && !/[\u3040-\u309F\u30A0-\u30FF]/.test(s)) {
    return "zh-CN";
  }
  if (/[äöüÄÖÜß]/.test(s)) {
    return "de-DE";
  }
  // If the visible probe text is plain Latin (no CJK), treat it as English.
  // This prevents stale non-English fallback when page translation is reset.
  if (!/[\u3040-\u30FF\u4E00-\u9FFF\uAC00-\uD7AF]/.test(s)) {
    return "en-US";
  }
  return "";
};

const getMeaningfulHtmlLang = () => {
  if (typeof document === "undefined") {
    return "";
  }
  const raw =
    document.documentElement?.lang || document.body?.lang || "";
  if (!raw.trim()) {
    return "";
  }
  if (isEnglishLang(raw)) {
    return "";
  }
  return isSupportedPiiPdfLanguage(raw) ? raw : "";
};

const inferLanguageFromDomProbe = () => {
  if (typeof document === "undefined") {
    return "";
  }
  const el = document.querySelector("[data-pdf-lang-probe]");
  return inferPdfLanguageFromProbeText(el?.textContent);
};

const preferNavigatorLanguage = () => {
  if (typeof window === "undefined" || !window.navigator) {
    return "";
  }
  if (isSupportedPiiPdfLanguage(window.navigator.language)) {
    return window.navigator.language;
  }
  const firstSupported = (window.navigator.languages || []).find((l) =>
    isSupportedPiiPdfLanguage(l)
  );
  return firstSupported || "";
};

/**
 * @param {string} appLanguage - Current i18n language (in-app selector)
 * @returns {string} Locale string for generatePiiReportPdf
 */
export function resolvePiiPdfLanguage(appLanguage) {
  if (typeof window === "undefined") {
    return appLanguage || "en-US";
  }

  const normalizedAppLanguage = normalizeLang(appLanguage);
  const appIsNonEnglish =
    isSupportedPiiPdfLanguage(normalizedAppLanguage) &&
    !isEnglishLang(normalizedAppLanguage);

  const translatorTarget = getBrowserTranslatorTargetLanguage();
  if (
    isSupportedPiiPdfLanguage(translatorTarget) &&
    !isEnglishLang(translatorTarget)
  ) {
    return translatorTarget;
  }

  const htmlLang = getMeaningfulHtmlLang();
  if (htmlLang) {
    return htmlLang;
  }

  const fromProbe = inferLanguageFromDomProbe();
  if (fromProbe && !isEnglishLang(fromProbe)) {
    return fromProbe;
  }

  const fromNav = preferNavigatorLanguage();
  if (fromNav && !isEnglishLang(fromNav)) {
    return fromNav;
  }

  if (appIsNonEnglish) {
    return appLanguage;
  }

  if (fromProbe) {
    return fromProbe;
  }

  if (isSupportedPiiPdfLanguage(fromNav)) {
    return fromNav;
  }

  return appLanguage || "en-US";
}
